import React from "react";
import Dialog from "@mui/material/Dialog";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import AddCircleRoundedIcon from "@mui/icons-material/AddCircleRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import useThemeMode from "../../helper/hooks/useThemeMode";
import { getActionColors, SEVERITY_COLOR_KEY } from "../../helper/theme/actionColors";

const ICONS = {
  add: AddCircleRoundedIcon,
  edit: EditRoundedIcon,
  delete: DeleteForeverRoundedIcon,
  warning: WarningRoundedIcon,
};

const DEFAULT_CONFIRM_LABEL = {
  add: "Add",
  edit: "Save",
  delete: "Delete",
  warning: "Confirm",
};

/**
 * Shared "are you sure?" dialog — same look everywhere in the app.
 * Use before every add/edit/delete action instead of window.confirm()
 * or a one-off custom <Dialog>.
 *
 *   <ConfirmDialog
 *     open={confirmOpen}
 *     severity="delete"                 // "add" | "edit" | "delete" | "warning"
 *     title="Delete this bank account?"
 *     message="This can't be undone."
 *     loading={deleting}
 *     onConfirm={handleActuallyDelete}
 *     onCancel={() => setConfirmOpen(false)}
 *   />
 */
export default function ConfirmDialog({
  open,
  severity = "warning",
  title,
  message,
  confirmLabel,
  cancelLabel = "Cancel",
  onConfirm,
  onCancel,
  loading = false,
}) {
  const { isDark } = useThemeMode();
  const C = getActionColors(isDark);
  const color = C[SEVERITY_COLOR_KEY[severity] || "warning"];
  const Icon = ICONS[severity] || ICONS.warning;

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onCancel}
      maxWidth="xs"
      fullWidth
      // Pages in this app layer their own custom overlays well above MUI's
      // default modal z-index (1300) — e.g. up to ~10002 for nested dropdowns
      // inside a product modal. A confirm prompt must always win that stack,
      // so it's pinned above anything else in the app can plausibly reach.
      sx={{ zIndex: 100000 }}
      PaperProps={{
        sx: {
          borderRadius: "20px",
          background: C.bg,
          animation: "csConfirmPop 0.28s cubic-bezier(0.34,1.56,0.64,1)",
        },
      }}
      BackdropProps={{ sx: { backdropFilter: "blur(4px)", background: "rgba(15,15,25,0.45)" } }}
    >
      <Box sx={{ p: 3.5, textAlign: "center" }}>
        <Box
          sx={{
            width: 64, height: 64, borderRadius: "18px", mx: "auto", mb: 2,
            background: `linear-gradient(145deg, ${color}, ${color}cc)`,
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: `0 12px 28px ${color}55, 0 4px 10px ${color}40, inset 0 1px 0 rgba(255,255,255,0.35)`,
            animation: "csIconPop 0.42s cubic-bezier(0.34,1.56,0.64,1) 0.05s both",
          }}
        >
          <Icon sx={{ fontSize: 32, color: "#fff" }} />
        </Box>
        <Typography sx={{ fontSize: "1.05rem", fontWeight: 800, color: `${C.text} !important`, mb: 0.8 }}>
          {title}
        </Typography>
        {message && (
          <Typography sx={{ fontSize: "0.86rem", color: `${C.muted} !important`, lineHeight: 1.5 }}>
            {message}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: "flex", gap: 1.2, px: 3, pb: 3 }}>
        <Box
          component="button"
          onClick={onCancel}
          disabled={loading}
          sx={{
            flex: 1, border: `1px solid ${C.border}`, background: "transparent",
            color: `${C.muted} !important`, fontWeight: 700, fontSize: "0.86rem",
            borderRadius: "12px", py: 1.2, cursor: loading ? "default" : "pointer",
          }}
        >
          {cancelLabel}
        </Box>
        <Box
          component="button"
          onClick={onConfirm}
          disabled={loading}
          sx={{
            flex: 1, border: "none", cursor: loading ? "default" : "pointer",
            background: `linear-gradient(135deg, ${color}, ${color}dd)`,
            color: "#fff !important", fontWeight: 700, fontSize: "0.86rem",
            borderRadius: "12px", py: 1.2,
            boxShadow: `0 6px 16px ${color}50`,
            display: "flex", alignItems: "center", justifyContent: "center", gap: 1,
            opacity: loading ? 0.75 : 1,
          }}
        >
          {loading && <CircularProgress size={15} sx={{ color: "#fff" }} />}
          {confirmLabel || DEFAULT_CONFIRM_LABEL[severity] || "Confirm"}
        </Box>
      </Box>
    </Dialog>
  );
}
